import { useState } from "react";
import dayjs from "dayjs";
import { CalendarPlus } from "lucide-react";
import type { CustomComponents, Matcher } from "react-day-picker";
import { cn } from "lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "components/ui/popover";
import { Calendar, CalendarDayButton } from "components/ui/calendar";
// Deep path, not the campaign-builder/state barrel: that barrel re-exports switchToEddm, which would
// pull graphQL mutations and the router into this chunk.
import { isBusinessDay } from "campaign-builder/state/launchDate";

const ERROR_ID = "eddmLaunchDateError";
const DISPLAY_FORMAT = "MMM D, YYYY";

const isWeekend = (date: Date) => !isBusinessDay(date);

// Module-level so react-day-picker doesn't remount the grid on every render.
const calendarComponents: Partial<CustomComponents> = {
  PreviousMonthButton: (props) => <button {...props} data-cypress-id="eddmLaunchDatePrevMonth" />,
  NextMonthButton: (props) => <button {...props} data-cypress-id="eddmLaunchDateNextMonth" />,
  CaptionLabel: (props) => <span {...props} data-cypress-id="eddmLaunchDateMonthLabel" />,
  // The cell already fades unavailable days to 50% (Figma); stop the disabled button fading them again to 25%.
  DayButton: (props) => (
    <CalendarDayButton
      {...props}
      className={cn(props.className, "disabled:opacity-100")}
      data-cypress-id={`eddmLaunchDateDay-${dayjs(props.day.date).format("YYYY-MM-DD")}`}
    />
  ),
};

export type LaunchDatePickerProps = {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  error?: string | null;
};

export const LaunchDatePicker = ({ value, onChange, minDate, maxDate, disabled, error }: LaunchDatePickerProps) => {
  const [open, setOpen] = useState(false);

  const unavailable: Matcher[] = [isWeekend];
  if (minDate) unavailable.push({ before: minDate });
  if (maxDate) unavailable.push({ after: maxDate });

  const handleSelect = (date: Date) => {
    onChange(date);
    setOpen(false);
  };

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            // @ts-expect-error legacy QA hook kept from the react-datepicker field; a literal so the anchors manifest sees it
            cypress_id="campaignStart-datePicker"
            data-cypress-id="eddmLaunchDatePickerButton"
            aria-label={value ? `Launch date: ${dayjs(value).format(DISPLAY_FORMAT)}` : "Select a launch date"}
            aria-invalid={!!error}
            aria-describedby={error ? ERROR_ID : undefined}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-[10px] border border-solid bg-background pl-3 pr-2 text-left text-sm focus:outline-none focus-visible:border-slate-400 focus-visible:ring-1 focus-visible:ring-slate-200 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:border-slate-500 dark:focus-visible:ring-slate-700",
              error ? "border-destructive" : "border-border"
            )}
          >
            <span className={value ? "text-foreground" : "text-muted-foreground"}>
              {value ? dayjs(value).format(DISPLAY_FORMAT) : "Select a date to launch"}
            </span>
            <CalendarPlus className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          // data-slot lets the shadcn Calendar drop its own background and sit on the popover surface.
          data-slot="popover-content"
          data-cypress-id="eddmLaunchDateCalendar"
          className="cb-v2-root w-auto rounded-lg p-0"
        >
          <Calendar
            mode="single"
            required
            selected={value ?? undefined}
            onSelect={handleSelect}
            defaultMonth={value ?? minDate}
            startMonth={minDate}
            endMonth={maxDate}
            disabled={unavailable}
            components={calendarComponents}
          />
        </PopoverContent>
      </Popover>
      {error && (
        <p id={ERROR_ID} className="mt-1 text-xs text-destructive" role="alert" data-cypress-id="eddmLaunchDateError">
          {error}
        </p>
      )}
    </div>
  );
};
